// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Group, Loader, Paper, Stack, Switch, TextInput, Title, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import { PageHeader } from '../../components/layout/PageHeader';
import type { PageTabsItem } from '../../components/layout/PageTabs';
import { PageTabs } from '../../components/layout/PageTabs';
import { DeniedTable } from './DeniedTable';
import { InactiveTable } from './InactiveTable';
import { IneligibleTable } from './IneligibleTable';
import { IntakeTable } from './IntakeTable';
import { TestingTable } from './TestingTable';
import { TreatmentMaintenanceStageCard } from './TreatmentMaintenanceStageCard';
import styles from './TriagePage.module.css';
import { useTriageQueueUrlState } from './useTriageQueueUrlState';

const PAGE_SIZE = 10;

const STAGES = ['All Stages', 'Intake', 'Refer out', 'Testing', 'Treatment maintenance', 'Ineligible', 'Inactive'];

const STAGE_TABS: PageTabsItem[] = STAGES.map((stage) => ({ id: stage, label: stage }));

const SECTION_TITLES: Record<string, string> = {
  'All Stages': 'All',
  Ineligible: 'Ineligible Due to Insurance',
  'Refer out': 'Refer out',
};

function getSectionTitle(stage: string): string {
  return SECTION_TITLES[stage] ?? stage;
}

export function TriagePage(): JSX.Element {
  const isCompact = useMediaQuery('(max-width: 1440px)');
  const isNarrow = useMediaQuery('(max-width: 1200px)');
  let paperPadding: 'sm' | 'md' | 'lg' = 'lg';
  if (isNarrow) {
    paperPadding = 'sm';
  } else if (isCompact) {
    paperPadding = 'md';
  }
  const [activeStage, setActiveStage] = useState('All Stages');
  const [intakeTotal, setIntakeTotal] = useState(0);
  const { sortState, page, search, setSortState, setPage, setSearch, hideTestPatients, setHideTestPatients } =
    useTriageQueueUrlState();
  const [searchInput, setSearchInput] = useState(search);
  const [intakePage, setIntakePage] = useState(1);
  const [referPage, setReferPage] = useState(1);
  const [testingPage, setTestingPage] = useState(1);
  const [ineligiblePage, setIneligiblePage] = useState(1);
  const [inactivePage, setInactivePage] = useState(1);
  const [inactiveTotal, setInactiveTotal] = useState(0);
  const [sectionSearches, setSectionSearches] = useState<Record<string, string>>({});
  const [referTotal, setReferTotal] = useState(0);

  const getSectionSearch = (section: string): string => sectionSearches[section] ?? '';
  const setSectionSearch = (section: string, value: string): void => {
    setSectionSearches((current) => ({ ...current, [section]: value }));
  };

  useEffect(() => {
    if (searchInput === search) {
      return;
    }
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search, setSearch]);

  return (
    <Stack gap="md" m="xs">
      <PageHeader
        title="Triage Queue"
        actions={
          <PageTabs
            tabs={STAGE_TABS}
            value={activeStage}
            onChange={(stage) => {
              setActiveStage(stage);
              setPage(1);
            }}
          />
        }
      />

      {activeStage === 'Treatment maintenance' ? (
        <TreatmentMaintenanceStageCard
          pageSize={PAGE_SIZE}
          searchInput={searchInput}
          sortState={sortState}
          onSearchInputChange={setSearchInput}
          onSortChange={setSortState}
          hideTestPatients={hideTestPatients}
          setHideTestPatients={setHideTestPatients}
        />
        // biome-ignore lint/style/noNestedTernary: stage-specific queue views are explicit and mutually exclusive
      ) : activeStage === 'All Stages' ? (
        <Stack gap="md">
          <QueueSection
            title="Intake"
            count={intakeTotal}
            searchInput={getSectionSearch('Intake')}
            onSearchChange={(value) => setSectionSearch('Intake', value)}
          >
            <IntakeTable
              page={intakePage}
              pageSize={PAGE_SIZE}
              search={getSectionSearch('Intake')}
              sortState={sortState}
              onPageChange={setIntakePage}
              onSortChange={setSortState}
              hideTestPatients={hideTestPatients}
              onTotalChange={setIntakeTotal}
            />
          </QueueSection>
          <QueueSection
            title="Refer out"
            count={referTotal}
            searchInput={getSectionSearch('Refer out')}
            onSearchChange={(value) => setSectionSearch('Refer out', value)}
          >
            <DeniedTable
              page={referPage}
              pageSize={PAGE_SIZE}
              search={getSectionSearch('Refer out')}
              onPageChange={setReferPage}
              sortState={sortState}
              onSortChange={setSortState}
              hideTestPatients={hideTestPatients}
              onTotalChange={setReferTotal}
            />
          </QueueSection>
          <QueueSection
            title="Testing"
            searchInput={getSectionSearch('Testing')}
            onSearchChange={(value) => setSectionSearch('Testing', value)}
          >
            <TestingTable
              page={testingPage}
              pageSize={PAGE_SIZE}
              search={getSectionSearch('Testing')}
              onPageChange={setTestingPage}
              sortState={sortState}
              onSortChange={setSortState}
              hideTestPatients={hideTestPatients}
            />
          </QueueSection>
          <TreatmentMaintenanceStageCard
            pageSize={PAGE_SIZE}
            searchInput={getSectionSearch('Treatment maintenance')}
            sortState={sortState}
            onSearchInputChange={(value) => setSectionSearch('Treatment maintenance', value)}
            onSortChange={setSortState}
            hideTestPatients={hideTestPatients}
            setHideTestPatients={setHideTestPatients}
          />
          <QueueSection
            title="Ineligible Due to Insurance"
            searchInput={getSectionSearch('Ineligible')}
            onSearchChange={(value) => setSectionSearch('Ineligible', value)}
          >
            <IneligibleTable
              page={ineligiblePage}
              pageSize={PAGE_SIZE}
              onPageChange={setIneligiblePage}
              sortState={sortState}
              onSortChange={setSortState}
              search={getSectionSearch('Ineligible')}
              hideTestPatients={hideTestPatients}
            />
          </QueueSection>
          <QueueSection
            title="Inactive"
            count={inactiveTotal}
            searchInput={getSectionSearch('Inactive')}
            onSearchChange={(value) => setSectionSearch('Inactive', value)}
          >
            <InactiveTable
              page={inactivePage}
              pageSize={PAGE_SIZE}
              search={getSectionSearch('Inactive')}
              onPageChange={setInactivePage}
              onTotalChange={setInactiveTotal}
              hideTestPatients={hideTestPatients}
            />
          </QueueSection>
        </Stack>
        // biome-ignore lint/style/noNestedTernary: stage-specific queue views are explicit and mutually exclusive
      ) : activeStage === 'Inactive' ? (
        <QueueSection
          title="Inactive"
          count={inactiveTotal}
          searchInput={getSectionSearch('Inactive')}
          onSearchChange={(value) => setSectionSearch('Inactive', value)}
        >
          <InactiveTable
            page={inactivePage}
            pageSize={PAGE_SIZE}
            search={getSectionSearch('Inactive')}
            onPageChange={setInactivePage}
            onTotalChange={setInactiveTotal}
            hideTestPatients={hideTestPatients}
          />
        </QueueSection>
      ) : (
        <Paper className={styles.section} withBorder radius="md" shadow="sm" p={paperPadding}>
          <Stack gap="md">
            <Group justify="space-between" align={isNarrow ? 'stretch' : 'center'} wrap="wrap">
              <Group gap="sm" align="center">
                <Title order={4}>{getSectionTitle(activeStage)}</Title>
                {activeStage === 'Intake' && (
                  <Badge
                    variant="light"
                    color="gray"
                    radius="xl"
                    size="lg"
                    styles={{ root: { textTransform: 'none' } }}
                  >
                    {intakeTotal}
                  </Badge>
                )}
              </Group>
              <Group gap="md" align="center" wrap="wrap" style={{ width: isNarrow ? '100%' : undefined }}>
                <Tooltip label="Hide test patients" withArrow openDelay={0}>
                  <span style={{ display: 'inline-flex' }}>
                    <Switch
                      aria-label="Hide test patients"
                      size="sm"
                      checked={hideTestPatients}
                      onChange={(e) => setHideTestPatients(e.currentTarget.checked)}
                    />
                  </span>
                </Tooltip>
                <TextInput
                  placeholder="Search"
                  leftSection={<IconSearch size={14} />}
                  rightSection={searchInput ? <Loader size={14} /> : undefined}
                  size="xs"
                  style={{ flex: isNarrow ? '1 1 220px' : undefined }}
                  w={isNarrow ? undefined : 200}
                  miw={isNarrow ? 220 : undefined}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                />
              </Group>
            </Group>

            {activeStage === 'Intake' && (
              <IntakeTable
                page={page}
                pageSize={PAGE_SIZE}
                search={search}
                sortState={sortState}
                onPageChange={setPage}
                onSortChange={setSortState}
                hideTestPatients={hideTestPatients}
                onTotalChange={setIntakeTotal}
              />
            )}

            {activeStage === 'Ineligible' && (
              <IneligibleTable
                page={page}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
                sortState={sortState}
                onSortChange={setSortState}
                search={search}
                hideTestPatients={hideTestPatients}
              />
            )}

            {activeStage === 'Testing' && (
              <TestingTable
                page={page}
                pageSize={PAGE_SIZE}
                search={search}
                onPageChange={setPage}
                sortState={sortState}
                onSortChange={setSortState}
                hideTestPatients={hideTestPatients}
              />
            )}

            {activeStage === 'Refer out' && (
              <DeniedTable
                page={page}
                pageSize={PAGE_SIZE}
                search={search}
                onPageChange={setPage}
                sortState={sortState}
                onSortChange={setSortState}
                hideTestPatients={hideTestPatients}
              />
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

interface QueueSectionProps {
  title: string;
  count?: number;
  searchInput: string;
  onSearchChange: (value: string) => void;
  children: JSX.Element;
}

function QueueSection({ title, count, searchInput, onSearchChange, children }: QueueSectionProps): JSX.Element {
  return (
    <Paper className={styles.section} withBorder radius="md" shadow="sm" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap">
          <Group gap="sm">
            <Title order={2}>{title}</Title>
            {count !== undefined && (
              <Badge color="gray" variant="light" radius="xl">
                {count}
              </Badge>
            )}
          </Group>
          <TextInput
            placeholder="Search"
            leftSection={<IconSearch size={14} />}
            size="sm"
            w={200}
            value={searchInput}
            onChange={(event) => onSearchChange(event.currentTarget.value)}
          />
        </Group>
        {children}
      </Stack>
    </Paper>
  );
}
